/**
 * Keeps the squiggle overlay pixel-aligned with the textarea underneath it,
 * mirroring its font, padding, border, size and scroll position.
 */

import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

const MIRRORED_STYLES = [
  "boxSizing",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "fontVariant",
  "letterSpacing",
  "lineHeight",
  "textAlign",
  "textIndent",
  "textTransform",
  "wordSpacing",
  "tabSize",
];

export default function useMirrorGeometry(value, ranges) {
  const wrapperRef = useRef(null);
  const mirrorRef = useRef(null);
  const antdRef = useRef(null);

  const getTextArea = useCallback(
    () =>
      antdRef.current?.resizableTextArea?.textArea ??
      wrapperRef.current?.querySelector("textarea") ??
      null,
    []
  );

  const alignMirror = useCallback(() => {
    const textArea = getTextArea();
    const mirror = mirrorRef.current;
    const wrapper = wrapperRef.current;
    if (!textArea || !mirror || !wrapper) return;

    const computed = window.getComputedStyle(textArea);
    MIRRORED_STYLES.forEach((property) => {
      mirror.style[property] = computed[property];
    });

    const textAreaBox = textArea.getBoundingClientRect();
    const wrapperBox = wrapper.getBoundingClientRect();
    mirror.style.top = `${textAreaBox.top - wrapperBox.top}px`;
    mirror.style.left = `${textAreaBox.left - wrapperBox.left}px`;
    mirror.style.width = `${textAreaBox.width}px`;
    mirror.style.height = `${textAreaBox.height}px`;
    mirror.scrollTop = textArea.scrollTop;
  }, [getTextArea]);

  const syncScroll = useCallback(() => {
    const textArea = getTextArea();
    if (textArea && mirrorRef.current) mirrorRef.current.scrollTop = textArea.scrollTop;
  }, [getTextArea]);

  useLayoutEffect(() => {
    alignMirror();
  }, [alignMirror, value, ranges]);

  useEffect(() => {
    const textArea = getTextArea();
    if (!textArea || typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(alignMirror);
    observer.observe(textArea);
    return () => observer.disconnect();
  }, [alignMirror, getTextArea]);

  return { wrapperRef, mirrorRef, antdRef, getTextArea, syncScroll };
}
